import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import ChangeHistory, { KindToggles } from './ChangeHistory';

const RECORD_TYPES = [
	{ key: 'Events', label: 'Events' },
	{ key: 'EventTemplates', label: 'Event templates' },
	{ key: 'Groups', label: 'Groups' },
	{ key: 'Members', label: 'Profiles' },
];
const ALL_KINDS = ['edit', 'notification', 'confirmation'];
const PAGE = 30;
const FILTERS_KEY = 'dashboard.activityHistory.filters';

/** per-viewer convenience: the filters survive Open -> Back */
const readFilters = () => {
	try { return JSON.parse(window.sessionStorage.getItem(FILTERS_KEY)) ?? {}; }
	catch { return {}; }
};
const writeFilters = (value) => {
	try { window.sessionStorage.setItem(FILTERS_KEY, JSON.stringify(value)); }
	catch { /* storage blocked */ }
};

const pathFor = (e) => {
	switch (e.collection) {
		case 'Events': return `/editor?id=${e.docId}&tab=History`;
		case 'EventTemplates': return `/template?id=${e.docId}`;
		case 'Groups': return `/group?id=${e.docId}`;
		case 'Members': return `/profile?id=${e.docId}`;
		default: return undefined;
	}
};

/** Dashboard: every change to events, templates, groups and profiles, newest first, decoded into Before | After */
export default function ActivityHistoryPanel() {
	const navigate = useNavigate();
	const saved = readFilters();
	const [types, setTypes] = useState(Array.isArray(saved.types) ? saved.types : RECORD_TYPES.map(t => t.key));
	const [kinds, setKinds] = useState(Array.isArray(saved.kinds) ? saved.kinds : ALL_KINDS);
	const [entries, setEntries] = useState([]);
	const [next, setNext] = useState(undefined);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState(null);
	const request = useRef(0);

	const fetchPage = useCallback(async (cursor) => {
		const reqId = ++request.current;
		cursor ? setLoadingMore(true) : setLoading(true);
		setError(null);
		try {
			if (types.length === 0 || kinds.length === 0) {
				setEntries([]);
				setNext(undefined);
				return;
			}
			const data = await api.getRecentHistory({ limit: PAGE, collections: types, kinds, next: cursor });
			if (reqId !== request.current) return;
			setEntries(prev => cursor ? [...prev, ...(data.entries ?? [])] : (data.entries ?? []));
			setNext(data.next);
		}
		catch (ex) {
			if (reqId === request.current) setError(ex);
		}
		finally {
			if (reqId === request.current) { setLoading(false); setLoadingMore(false); }
		}
	}, [types, kinds]);

	useEffect(() => { fetchPage(undefined); }, [fetchPage]);
	useEffect(() => { writeFilters({ types, kinds }); }, [types, kinds]);

	return (
		<div>
			<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, paddingBottom: 10 }}>
				<div style={{ fontSize: 18, fontWeight: 'bold' }}>Activity History</div>
				<Button basic compact size='small' icon='refresh' content='Refresh' onClick={() => fetchPage(undefined)} disabled={loading} />
			</div>
			<div className='ch-panel-tools' style={{ justifyContent: 'flex-start' }}>
				<div className='ch-toggles' role='group' aria-label='Show records'>
					{RECORD_TYPES.map(t => {
						const active = types.includes(t.key);
						return (
							<button key={t.key} type='button' aria-pressed={active} className={`ch-toggle ch-kind-edit ${active ? 'ch-toggle-on' : ''}`}
								onClick={() => setTypes(active ? types.filter(x => x !== t.key) : [...types, t.key])}>
								<Icon name={active ? 'check square' : 'square outline'} />
								{t.label}
							</button>
						);
					})}
				</div>
				<KindToggles kinds={kinds} onChange={setKinds} />
			</div>
			<ChangeHistory
				entries={entries}
				kinds={kinds}
				loading={loading}
				error={error}
				showTarget
				onOpenTarget={(e) => { const p = pathFor(e); if (p) navigate(p); }}
				hasMore={!!next}
				loadingMore={loadingMore}
				onLoadMore={() => fetchPage(next)}
				emptyText={types.length === 0 || kinds.length === 0
					? 'Turn on at least one record type and one kind of change.'
					: 'No changes yet. Changes to events, templates, groups and profiles show up here as they are saved.'}
			/>
		</div>
	);
}
